/**
 * Notifications Sheet Component
 * Displays notifications in a sheet (side panel) for all screen sizes
 * Based on Figma design: Feed Sidebar with tabs
 * Supports both controlled (with open/onOpenChange props) and uncontrolled modes
 */

import { useState } from "react"
import { BellIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { formatNotificationTime } from "@/utils/formatTime"
import { useNotifications, useUnreadCount } from "../hooks/useNotifications"
import type { Notification, NotificationTab } from "../services/notification.service"
import { AddedToCommunityNotification } from "./AddedToCommunityNotification"
import { BroadcastNotification } from "./BroadcastNotification"
import { CommentPostedNotification } from "./CommentPostedNotification"
import { CommunityInviteNotification } from "./CommunityInviteNotification"
import { CommunityJoinedNotification } from "./CommunityJoinedNotification"
import { CommunityLiveNotification } from "./CommunityLiveNotification"
import { FollowRequestNotification } from "./FollowRequestNotification"
import { FollowRequestStatusNotification } from "./FollowRequestStatusNotification"
import { JoinRequestNotification } from "./JoinRequestNotification"
import { JoinStatusNotification } from "./JoinStatusNotification"
import { NewFollowerNotification } from "./NewFollowerNotification"
import { NotificationInput } from "./NotificationInput"
import { NotificationTabs } from "./NotificationTabs"
import { RitualCompletedNotification } from "./RitualCompletedNotification"
import { RitualInviteNotification } from "./RitualInviteNotification"
import { RitualJoinRequestNotification } from "./RitualJoinRequestNotification"
import { RitualJoinRequestStatusNotification } from "./RitualJoinRequestStatusNotification"
import { RitualLiveNotification } from "./RitualLiveNotification"
import { RitualMemberInviteNotification } from "./RitualMemberInviteNotification"
import { RitualMemberJoinedNotification } from "./RitualMemberJoinedNotification"
import { SessionBookedNotification } from "./SessionBookedNotification"
import { SessionLiveNotification } from "./SessionLiveNotification"

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

interface NotificationsSheetProps {
  /** External controlled state (optional - component uses internal state if not provided) */
  open?: boolean
  /** Callback when sheet open state changes */
  onOpenChange?: (open: boolean) => void
}

export function NotificationsSheet({ open: externalOpen, onOpenChange }: NotificationsSheetProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<NotificationTab>("all")
  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const currentUserId = useAuthStore(state => state.user?.id)

  const handleOpenChange = (newOpen: boolean) => {
    if (externalOpen === undefined) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }

  const {
    notifications,
    markAsRead,
    markAsUnread,
    deleteBroadcast,
    handleNotificationClick,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNotifications({
    enabled: open,
    tab: activeTab,
    onAfterNavigate: () => handleOpenChange(false),
  })

  // Get unread count for badge (always enabled)
  const { data: unreadBadgeCount } = useUnreadCount("all")

  // In uncontrolled mode (no external props), show bell icon trigger
  const isUncontrolled = externalOpen === undefined

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: () => {
      void fetchNextPage()
    },
    hasMore: hasNextPage || false,
    disabled: isFetchingNextPage || isLoading,
  })

  return (
    <>
      {/* Bell icon trigger (only in uncontrolled mode) */}
      {isUncontrolled && (
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-lg h-[100px]"
          aria-label="Notifications"
          onClick={() => handleOpenChange(true)}
        >
          <BellIcon className="!w-[20px] !h-[20px]" />
          {(unreadBadgeCount || 0) > 0 && (
            <span className="absolute top-7 right-0 w-5 h-5 bg-brand-terracotta text-white text-xs rounded-full flex items-center justify-center">
              {(unreadBadgeCount || 0) > 9 ? "9+" : unreadBadgeCount}
            </span>
          )}
        </Button>
      )}

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[740px] p-0 overflow-y-auto flex flex-col bg-[#0E0E0E] px-7 pb-5"
        >
          {/* Header Section */}
          <div className="pt-5">
            <h1 className="text-[28px] font-semibold uppercase text-primary tracking-wide">Feed</h1>
            <p className="text-[14px] text-stone leading-[25px]">
              Updates from your practice and people you follow
            </p>
          </div>

          {/* Input Field */}
          <div className="mt-0">
            <NotificationInput />
          </div>

          {/* Sticky Tab Bar */}
          <div className="sticky top-0 z-10 bg-[#0E0E0E] border-b border-[#F2F0EB]/10">
            <NotificationTabs activeTab={activeTab} onTabChange={setActiveTab} />
          </div>

          {/* Notification List */}
          <div className="bg-[#0E0E0E]">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-stone text-sm">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12 text-stone text-sm">No notifications</div>
            ) : (
              <>
                <div className="mt-1 space-y-2">
                  {notifications.map(notification => {
                    if (notification.action === "added_to_community") {
                      return (
                        <div key={notification.id} className="">
                          <AddedToCommunityNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                          />
                        </div>
                      )
                    }

                    if (notification.action === "join_request_received") {
                      if (notification.type === "ritual") {
                        return (
                          <div key={notification.id} className="">
                            <RitualJoinRequestNotification
                              notification={notification}
                              onRead={() => markAsRead(notification.id)}
                              onUnread={() => markAsUnread(notification.id)}
                            />
                          </div>
                        )
                      }
                      return (
                        <div key={notification.id} className="">
                          <JoinRequestNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                          />
                        </div>
                      )
                    }

                    if (notification.action === "community_joined") {
                      return (
                        <div key={notification.id} className="">
                          <CommunityJoinedNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                          />
                        </div>
                      )
                    }

                    if (notification.action === "community_live") {
                      return (
                        <CommunityLiveNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                        />
                      )
                    }

                    if (
                      notification.action === "join_request_accepted" ||
                      notification.action === "join_request_rejected"
                    ) {
                      if (notification.type === "ritual") {
                        return (
                          <div key={notification.id} className="">
                            <RitualJoinRequestStatusNotification
                              notification={notification}
                              onRead={() => markAsRead(notification.id)}
                              onUnread={() => markAsUnread(notification.id)}
                            />
                          </div>
                        )
                      }
                      return (
                        <div key={notification.id} className="">
                          <JoinStatusNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                          />
                        </div>
                      )
                    }

                    if (notification.action === "invitation_sent") {
                      return (
                        <CommunityInviteNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                        />
                      )
                    }

                    if (
                      notification.type === "ritual" &&
                      notification.action === "organizer_invitation_sent"
                    ) {
                      return (
                        <div key={notification.id} className="">
                          <RitualInviteNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                          />
                        </div>
                      )
                    }

                    if (
                      notification.type === "ritual" &&
                      notification.action === "member_invitation_sent"
                    ) {
                      return (
                        <div key={notification.id} className="">
                          <RitualMemberInviteNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                          />
                        </div>
                      )
                    }

                    if (notification.action === "ritual_live") {
                      return (
                        <RitualLiveNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (notification.action === "ritual_member_joined") {
                      return (
                        <RitualMemberJoinedNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (notification.action === "session_live") {
                      return (
                        <SessionLiveNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (notification.action === "session_booked") {
                      return (
                        <SessionBookedNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (notification.action === "ritual_completed") {
                      return (
                        <RitualCompletedNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (notification.action === "community_added_to_ritual") {
                      return (
                        <RitualCompletedNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (
                      notification.action === "comment_posted" ||
                      notification.action === "comment_reply" ||
                      notification.action === "comment_mention"
                    ) {
                      return (
                        <CommentPostedNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    // Broadcast notifications (Friends tab)
                    if (notification.action === "broadcast") {
                      return (
                        <BroadcastNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onDelete={
                            notification.triggeredByUser?.id === currentUserId
                              ? () => deleteBroadcast(notification.id)
                              : undefined
                          }
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    // Profile notifications (Friends tab)
                    if (notification.action === "new_follower") {
                      return (
                        <NewFollowerNotification
                          notification={notification}
                          onRead={() => markAsRead(notification.id)}
                          onUnread={() => markAsUnread(notification.id)}
                          onNotificationClick={handleNotificationClick}
                        />
                      )
                    }

                    if (notification.action === "follow_request_received") {
                      return (
                        <div key={notification.id} className="p-2">
                          <FollowRequestNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                            onNotificationClick={handleNotificationClick}
                          />
                        </div>
                      )
                    }

                    if (
                      notification.action === "follow_request_accepted" ||
                      notification.action === "follow_request_rejected"
                    ) {
                      return (
                        <div key={notification.id} className="p-2">
                          <FollowRequestStatusNotification
                            notification={notification}
                            onRead={() => markAsRead(notification.id)}
                            onUnread={() => markAsUnread(notification.id)}
                            onNotificationClick={handleNotificationClick}
                          />
                        </div>
                      )
                    }

                    return (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`flex flex-col p-4 rounded-md cursor-pointer transition-colors border ${
                          !notification.isRead ? "notification-unread" : "notification-read"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-card flex-shrink-0">
                            {notification.triggeredByUser &&
                            getUserDisplayImage(notification.triggeredByUser) ? (
                              <img
                                src={getUserDisplayImage(notification.triggeredByUser)}
                                alt={notification.triggeredByUser.name || "User"}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-[11px]">
                                {notification.triggeredByUser?.name?.[0] || "?"}
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-primary text-[11px]">
                                {notification.triggeredByUser?.name || "User"}
                              </span>
                              {notification.triggeredByUser?.username && (
                                <span className="text-stone/50 text-[11px]">
                                  @{notification.triggeredByUser.username}
                                </span>
                              )}
                              {!notification.isRead && (
                                <div className="w-2 h-2 rounded-full bg-brand-purple flex-shrink-0" />
                              )}
                            </div>

                            <p className="text-[11px] text-primary mt-2">{notification.message}</p>

                            <p className="text-xs text-stone/50 mt-1">
                              {formatNotificationTime(notification.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div ref={loadMoreRef} className="h-1" />
                {isFetchingNextPage && (
                  <div className="text-center py-4 text-stone text-sm">Loading more...</div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
